import { PencilIcon, PlusIcon, TrashIcon } from 'lucide-react';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router';
import IconButton from './IconButton';
import React from 'react';

/**
 * @typedef {Object} School
 * @property {string} _id - The unique database ID for the school.
 * @property {string} id - A user-facing ID or short code for the school.
 * @property {string} name - The full name of the school.
 * @property {string} logo - URL for the school's logo image.
 * // Add any other expected school properties here.
 */

/**
 * @typedef {Object} SchoolListProps
 * @property {School[]} schools - An array of school objects to be displayed.
 * @property {() => void} onAdd - Callback function to initiate the 'Add New School' operation.
 * @property {(school: School) => void} onEdit - Callback function to initiate the 'Edit School' operation, passing the school object.
 * @property {(schoolId: string, schoolName: string) => void} onDelete - Callback function to initiate the 'Delete School' operation, passing the school ID and name.
 * @property {boolean} [isLoading] - Indicates if the data is currently being fetched.
 * @property {string | null} [error] - Contains an error message if data fetching failed.
 */

/**
 * Displays a list of school configurations with options to add, edit, and delete.
 * Includes status messages for loading and errors.
 *
 * @param {SchoolListProps} props - The component props.
 * @returns {JSX.Element} The rendered list component.
 */
const SchoolList = ({ schools, onAdd, onEdit, onDelete, isLoading, error }) => {
    /**
     * @type {import('react-router').NavigateFunction}
     */
    const navigate = useNavigate();

    return (
        <div className="p-1 w-full">
            <header className="flex items-center justify-between mb-6">
                <div className='flex items-center justify-between gap-1.5'>
                    <h2 className="text-2xl font-semibold text-white  flex gap-3 cursor-pointer">
                        {/* Back Button */}
                        <span className='mt-1 ' onClick={() => navigate(-1)}>
                            <FaArrowLeft />
                        </span>
                    </h2>
                    <h2 className="text-2xl font-bold text-[var(--primary-text-color)]">School Configurations</h2>
                </div>
                {/* Add Button */}
                <IconButton
                    icon={PlusIcon}
                    bgColor='bg-[var(--analytics-card-color)]'
                    label="Add New School"
                    onClick={() => onAdd()}
                    className="bg-[var(--color-primary)] hover:bg--[var(--color-primary-dark)] text-white cursor-pointer"
                />
            </header>

            {/* Status Messages */}
            {isLoading && <div className="text-gray-400">Loading school data...</div>}
            {error && <div className="text-red-400">Error fetching data: {error}</div>}

            {!isLoading && schools.length === 0 && <div className="text-gray-400">No school configurations found.</div>}

            {/* School List */}
            <div className="space-y-4">
                {schools.map((school) => (
                    <div key={school._id} className="flex items-center justify-between p-4 bg-[var(--analytics-card-color)] rounded-xl border border-gray-700 hover:border-[#7436D2] transition-colors">
                        <div className="flex items-center space-x-4">
                            <img src={school.logo} alt={`${school.name} Logo`} className="h-10 w-10 object-contain rounded-full bg-black/10 p-1" />
                            <div>
                                <p className="text-lg font-semibold text-[var(--primary-text-color)]">{school.name}</p>
                                <p className="text-sm text-gray-400">ID: {school.id}</p>
                            </div>
                        </div>
                        <div className="flex space-x-2">
                            {/* Edit Button */}
                            <IconButton
                                icon={PencilIcon}
                                label="Update"
                                onClick={() => onEdit(school)}
                                className="text-[var(--color-primary)] hover:bg-orange-400/20 bg-transparent cursor-pointer"
                            />
                            {/* Delete Button */}
                            <IconButton
                                icon={TrashIcon}
                                label="Delete"
                                onClick={() => onDelete(school._id, school.name)}
                                className="text-red-400 hover:bg-red-900/50 bg-transparent cursor-pointer"
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default SchoolList;